import get from 'lodash/get';
import {
  ApolloClient,
  InMemoryCache,
  from,
  Observable,
  ApolloLink,
  NormalizedCacheObject,
  FetchResult,
  Operation,
  split,
  type FieldMergeFunction,
  type FieldPolicy,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition, relayStylePagination } from '@apollo/client/utilities';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { NextLink } from '@apollo/client/link/core';
import { createClient as createWsClient, type Client as GraphQLWsClient } from 'graphql-ws';
import { jwtDecode } from 'jwt-decode';
import { mergeInboxMessages } from '@arthelper/shared/inbox-messages-cache';
import { GRAPHQL_POSSIBLE_TYPES } from '@arthelper/shared';
import { mergeConnection } from '@arthelper/shared/image-library';
import { isWaitlistedError, redirectToWaitlist } from './waitlist-redirect';

const URI = import.meta.env.VITE_API_URL;

// relayStylePagination appends a page whose `after` cursor is missing from the list to the whole
// list, so a chat seen on two pages would list twice: keep the first edge per node.
function dedupedPagination(keyArgs: string[]): FieldPolicy {
  const base = relayStylePagination(keyArgs);
  const merge = base.merge as FieldMergeFunction;

  return {
    ...base,
    merge(existing, incoming, options) {
      const merged = merge(existing, incoming, options);
      const seen = new Set<string>();
      const edges = (merged.edges ?? []).filter((edge: { node?: { __ref?: string } }) => {
        const ref = edge.node?.__ref;
        if (!ref) {
          return true;
        }

        if (seen.has(ref)) {
          return false;
        }

        seen.add(ref);
        return true;
      });

      return { ...merged, edges };
    },
  };
}

let currentHash: string | null = null;
let profileRefetchCallback: (() => void) | null = null;

const headerLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const context = operation.getContext();
    const headers = context.response.headers;

    // Access and process headers here
    const hash = headers.get('x-permissions-hash');

    if (hash !== currentHash) {
      currentHash = hash;
      if (profileRefetchCallback) {
        profileRefetchCallback();
      }
    }

    return response;
  });
});

/** @todo refactor to avoid setting setProfileRefetchCallback to Appollo client instance and avoid such hack  */
export interface ExtendedClient extends ApolloClient<NormalizedCacheObject> {
  setProfileRefetchCallback: any;
  updateToken: (newToken: string | null) => void;
  updateRefreshToken: (newRefreshToken: string) => void;
}

function accessTokenPrincipal(token: string | null | undefined): string | null {
  if (!token) {
    return null;
  }
  try {
    const payload = jwtDecode<{ user_id?: unknown; sub?: unknown; asfId?: unknown }>(token);
    const id = payload.user_id ?? payload.sub ?? payload.asfId;
    return id != null ? String(id) : token;
  } catch {
    return token;
  }
}

export const createClient = ({
  token,
  refreshToken,
  getRefreshToken,
  signout = () => alert('Logout'),
  getClerkToken = async () => null,
}: any) => {
  // Store the latest token values
  let currentToken = token;
  let currentRefreshToken = refreshToken;

  const cache = new InMemoryCache({
    possibleTypes: GRAPHQL_POSSIBLE_TYPES,
    typePolicies: {
      Query: {
        fields: {
          /**
           * Offset pagination for the messaging dock's conversation list.
           *
           * keyArgs deliberately omits limit/offset so successive pages accumulate into
           * one cache entry, and deliberately KEEPS channel/search so a search is its own
           * list rather than being appended to the unfiltered inbox.
           *
           * Merge behavior lives in `mergeInboxMessages` (shared with town-square). A dock
           * mount refetches page 0; that write keeps rows already paged in. A reply
           * refetch keeps that tail only when the watch sets `refetchWritePolicy` to
           * `INBOX_MESSAGES_REFETCH_WRITE_POLICY`; otherwise Apollo passes an empty
           * existing page and this merge stores only the incoming slice.
           */
          messages: {
            keyArgs: ['channel', 'search'],
            merge(existing, incoming, { args }) {
              return mergeInboxMessages(existing, incoming, args);
            },
          },
          user: {
            merge(existing, incoming) {
              return { ...existing, ...incoming };
            },
          },
          // One cache entry per filter + sort; `after` pages append with node-id dedupe.
          myImages: {
            keyArgs: ['filter', 'sort'],
            merge(existing, incoming, { args, readField }) {
              return mergeConnection(existing, incoming, {
                after: args?.after,
                idOf: (edge: any) => readField<string>('id', edge.node),
              });
            },
          },
          // Keyset pages of the viewer's chats: a request without `after` (first page or a
          // refetch) replaces the list; a fetchMore page appends after its cursor.
          myChats: dedupedPagination(['type', 'targetId']),
        },
      },
      User: {
        keyFields: ['asfId'],
      },
      Image: {
        keyFields: ['id'],
        fields: {
          uploadProgress: {
            read(existing) {
              return existing || 0;
            },
          },
          uploadError: {
            read(existing) {
              return existing || null;
            },
          },
          preview: {
            read(existing) {
              return existing || null;
            },
          },
          tags: {
            merge(existing = [], incoming = []) {
              if (incoming.length === 0) return existing;

              return incoming;
            },
          },
        },
      },
      Collection: {
        keyFields: ['id'],
      },
      // Unkeyed and selected in different shapes (`{ count }` vs `{ count totalBytes }`)
      // under the same `imageStats` field; merging keeps one document from evicting the
      // other's fields and re-fetching it.
      ImageStats: {
        merge: true,
      },
      PlatformFeedCard: {
        keyFields: ['id'],
      },
    },
  });

  // Create a mutable auth link that can be updated
  const authLink = setContext(async (_, { headers }) => {
    // Try legacy token first
    if (currentToken) {
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${currentToken}`,
          'apollo-require-preflight': 'true',
        },
      };
    }

    // Fall back to Clerk session token
    const clerkToken = await getClerkToken();
    if (clerkToken) {
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${clerkToken}`,
          'x-clerk-token': 'true',
          'apollo-require-preflight': 'true',
        },
      };
    }

    return { headers };
  });

  // Shared token refresh logic
  const handleTokenRefresh = (operation: Operation, forward: NextLink): Observable<FetchResult> | undefined => {
    if (!currentRefreshToken || !currentToken) {
      // Don't sign out - this is likely a Clerk user or temporary network issue
      // Clerk manages sessions automatically, legacy JWT users continue below
      return;
    }

    return new Observable<FetchResult>((observer) => {
      getRefreshToken(currentRefreshToken)
        .then((tokenData: any) => {
          if (!tokenData) {
            console.log('No token data returned, signing out');
            signout();
            observer.error(new Error('Failed to refresh token'));
            return;
          }

          // Update the current token in the auth link
          currentToken = tokenData.access_token;

          // Retry the operation with new token
          operation.setContext({
            headers: {
              ...operation.getContext().headers,
              authorization: `Bearer ${tokenData.access_token}`,
            },
          });

          // Retry the request with the new token
          forward(operation).subscribe({
            next: (result: FetchResult) => {
              observer.next(result);
            },
            error: (error: any) => {
              observer.error(error);
            },
            complete: () => {
              observer.complete();
            },
          });
        })
        .catch((error: any) => {
          console.error('Error during token refresh:', error);
          signout();
          observer.error(error);
        });
    });
  };

  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) =>
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations, null, 2)}, Path: ${path}`
        )
      );

      // Signed in but still behind the signup gate: town-square's /waitlist holds her.
      if (isWaitlistedError(graphQLErrors)) {
        redirectToWaitlist();
        return;
      }

      // Check if any error is an authentication error
      if (graphQLErrors.some((e) => /not authenticated/gi.test(e.message))) {
        console.log('Not authenticated');
        const result = handleTokenRefresh(operation, forward);
        if (result) return result;
      }
    }

    if (networkError) {
      console.log(`[Network error]: ${networkError}`);

      // Handle different types of network errors
      if ((networkError as any).statusCode === 401) {
        console.log('Network error 401 - attempting token refresh');
        const result = handleTokenRefresh(operation, forward);
        if (result) return result;
      }

      if (get(networkError, 'message') === 'invalid signature') {
        signout();
        return;
      }
    }
  });

  const uploadLink = createUploadLink({
    uri: URI,
  });

  const httpLink = from([errorLink, authLink, headerLink, uploadLink]);

  // GraphQL subscriptions ride graphql-ws on the same `/graphql` path (http→ws, https→wss).
  const wsClient: GraphQLWsClient = createWsClient({
    url: (URI || '').replace(/^http/i, 'ws'),
    lazy: true,
    // infra sets no ALB idleTimeout → AWS default 60s idle kills silent sockets; the 30s ping is load-bearing
    keepAlive: 30_000,
    // A rolling deploy takes the API down for longer than the default 5 attempts
    // of randomised backoff survive, so the first attempts land while nothing is
    // listening. Retry for minutes instead, and cap the wait so a client that
    // reconnects late still reconnects soon. Fatal close codes (4401 and friends)
    // are still terminal — graphql-ws classifies those regardless of shouldRetry.
    retryAttempts: 20,
    retryWait: async (retries: number) => {
      const backoff = Math.min(10_000, 500 * 2 ** retries);
      await new Promise((resolve) => setTimeout(resolve, backoff + Math.floor(Math.random() * 500)));
    },
    // connectionParams reads CURRENT token at connect time; updateToken mutates
    // that binding and terminates the socket on identity change so reconnect
    // picks up fresh params.
    connectionParams: async () => {
      if (currentToken) {
        return { authorization: `Bearer ${currentToken}` };
      }

      const clerkToken = await getClerkToken();
      if (clerkToken) {
        return {
          authorization: `Bearer ${clerkToken}`,
          'x-clerk-token': 'true',
        };
      }

      // Never open the socket without a token. An unauthenticated connect makes the
      // server close 4401, which graphql-ws classifies as FATAL and never retries, so
      // live delivery would stay dead until the next reload. Throwing instead is not a
      // CloseEvent, so it skips that fatal branch: it surfaces to the listener, which
      // resubscribes on backoff once a token is available again.
      throw new Error('No auth token yet for the GraphQL subscription socket');
    },
  });

  const wsLink = new GraphQLWsLink(wsClient);

  const restartGraphQLSubscriptionSocket = () => {
    try {
      wsClient.terminate();
    } catch {
      // no open GraphQL subscriptions socket
    }
  };

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    httpLink
  );

  const client = new ApolloClient({
    link: splitLink,
    cache,
  }) as ExtendedClient;

  client.setProfileRefetchCallback = (callback: () => void) => {
    profileRefetchCallback = callback;
  };

  // Add method to update token without recreating client
  client.updateToken = (newToken: string | null) => {
    const previousPrincipal = accessTokenPrincipal(currentToken);
    currentToken = newToken || null;
    const nextPrincipal = accessTokenPrincipal(currentToken);
    // Sign-out always terminates (newToken null) so a Clerk-only socket drops
    // even when currentToken was already empty. Same-principal refresh stays.
    if (newToken == null || newToken === '' || previousPrincipal !== nextPrincipal) {
      restartGraphQLSubscriptionSocket();
    }
  };

  // Add method to update refresh token
  client.updateRefreshToken = (newRefreshToken: string) => {
    currentRefreshToken = newRefreshToken;
  };

  return client;
};
